import { Injectable, inject } from '@angular/core';
import { Observable, defer, from, of, throwError } from 'rxjs';
import { finalize, map, shareReplay } from 'rxjs/operators';
import { firstValueFrom } from 'rxjs';
import { CrmAuthApiService } from './crm-auth-api.service';
import { CrmAuthCookieService } from './crm-auth-cookie.service';

/** Web Locks name. Scoped per-origin, so every tab of this app contends for it. */
const REFRESH_LOCK_NAME = 'crm.token-refresh';

/**
 * How much life an access token needs for a queued refresh to stand down.
 *
 * A caller that waited behind the lock re-reads the cookie first: if another tab
 * (or another caller in this tab) already refreshed, the new token is sitting
 * there and calling the endpoint again would burn a refresh token for nothing.
 * Comfortably above the 60s buffer the expiry service refreshes at, so a token
 * that is genuinely due still gets refreshed.
 */
const ALREADY_REFRESHED_MS = 90_000;

/**
 * The single owner of `POST /auth/refresh` for the CRM.
 *
 * The backend rotates refresh tokens — every successful refresh returns a new
 * one and invalidates the old — so two refreshes racing on the same token means
 * the loser gets a 401 and the user is logged out of a session that was
 * perfectly healthy. There are two callers that can each fire independently:
 *
 *   - CrmTokenExpiryService, proactively, on its 30s poll and on `visibilitychange`
 *   - crmAuthErrorInterceptor, reactively, when a request comes back 401
 *
 * Returning to a backgrounded tab triggers both at once, which is exactly the
 * case the visibility handler was added for. Two open tabs race by default.
 *
 * This coordinator closes both gaps:
 *
 *   - within a tab, concurrent callers share one in-flight request
 *   - across tabs, Web Locks serialises them, and whoever gets the lock second
 *     finds the refreshed token already in the (shared) cookie jar and reuses it
 *
 * Callers keep their own failure handling — this only guarantees that at most
 * one refresh is in flight per browser at a time.
 */
@Injectable({ providedIn: 'root' })
export class CrmTokenRefreshCoordinator {
  private readonly api = inject(CrmAuthApiService);
  private readonly cookieService = inject(CrmAuthCookieService);

  private inFlight: Observable<string> | null = null;

  /**
   * Refreshes the access token, or joins the refresh already running.
   *
   * Emits the usable access token. Errors when there is no refresh token, when
   * it has already expired, or when the endpoint rejects the exchange — in every
   * one of those cases the session cannot be recovered and the caller should end it.
   */
  refresh(): Observable<string> {
    if (this.inFlight) {
      return this.inFlight;
    }

    this.inFlight = defer(() => this.runExclusively()).pipe(
      // Clears the slot as soon as this cycle settles, so a later 401 starts a
      // fresh refresh rather than replaying this one's result forever.
      finalize(() => {
        this.inFlight = null;
      }),
      shareReplay({ bufferSize: 1, refCount: false }),
    );

    return this.inFlight;
  }

  /** Serialises across tabs where Web Locks exists, and degrades to the
   *  in-tab-only guarantee where it does not. */
  private runExclusively(): Observable<string> {
    const locks = typeof navigator !== 'undefined' ? navigator.locks : undefined;

    if (!locks) {
      return this.refreshUnlessAnotherTabAlreadyDid();
    }

    // `await` rather than returning the call directly: LockManager.request is
    // typed to resolve to whatever the callback returns, so handing it a promise
    // gives a Promise<Promise<string>> that only the await unwraps.
    const acquireAndRefresh = async (): Promise<string> =>
      await locks.request(REFRESH_LOCK_NAME, () =>
        firstValueFrom(this.refreshUnlessAnotherTabAlreadyDid()),
      );

    return from(acquireAndRefresh());
  }

  private refreshUnlessAnotherTabAlreadyDid(): Observable<string> {
    const accessToken = this.cookieService.getAccessToken();
    const accessTokenExpiresAt = this.cookieService.getAccessTokenExpiresAt();
    const alreadyRefreshed =
      !!accessToken &&
      accessTokenExpiresAt !== null &&
      accessTokenExpiresAt - Date.now() > ALREADY_REFRESHED_MS;

    return alreadyRefreshed ? of(accessToken) : this.callRefreshEndpoint();
  }

  private callRefreshEndpoint(): Observable<string> {
    const refreshToken = this.cookieService.getRefreshToken();
    const refreshTokenExpiresAt = this.cookieService.getRefreshTokenExpiresAt();

    if (!refreshToken) {
      return throwError(() => new Error('No refresh token available.'));
    }

    if (refreshTokenExpiresAt !== null && refreshTokenExpiresAt <= Date.now()) {
      return throwError(() => new Error('Refresh token has expired.'));
    }

    return this.api.refreshToken({ refreshToken }).pipe(
      map((response) => {
        const succeeded = Boolean(response?.isSuccess || response?.success);
        const data = response?.data;

        if (!succeeded || !data?.accessToken || !data?.refreshToken) {
          throw new Error('Refresh was rejected.');
        }

        this.cookieService.setAuthCookies({
          accessToken: data.accessToken,
          refreshToken: data.refreshToken,
          accessTokenExpiresUtc: data.accessTokenExpiresUtc,
          refreshTokenExpiresUtc: data.refreshTokenExpiresUtc,
        });

        return data.accessToken;
      }),
    );
  }
}
